import type {
  CountdownConfig,
  CountdownLabels,
  CountdownUnit,
  DayLabels,
} from "./Countdown.shared";
import {
  BaseCountdownStatic,
  calculateState,
  getVisibleUnits,
  normalizeDayLabels,
} from "./Countdown.shared";
import {
  ATTR_COUNTDOWN_UNIT,
  SELECTOR_TAG_LABEL,
  SELECTOR_TAG_UNIT,
  SELECTOR_TAG_VALUE,
} from "./constants";

interface CountdownTagUnit {
  element: HTMLElement;
  unit: CountdownUnit;
  valueElement: HTMLElement | null;
}

const defaultDayLabels: DayLabels = {
  many: "d",
  few: "d",
  one: "d",
};

const defaultLabels: Required<CountdownLabels> = {
  days: defaultDayLabels,
  hours: "h",
  minutes: "m",
  seconds: "s",
};

const defaultConfig: CountdownConfig = {
  targetDate: "",
  units: ["days", "hours", "minutes", "seconds"],
  labels: defaultLabels,
};

const getUnitLabel = (
  unit: CountdownUnit,
  labels: Required<CountdownLabels>,
): string => {
  if (unit === "days") {
    return normalizeDayLabels(labels.days, defaultDayLabels).many;
  }

  return labels[unit];
};

const formatValue = (value: number): string =>
  value < 100
    ? String(Math.max(0, value)).padStart(2, "0")
    : String(Math.max(0, value));

export default class CountdownTagStatic extends BaseCountdownStatic {
  units!: Map<CountdownUnit, CountdownTagUnit>;

  constructor(element: HTMLElement, config?: Partial<CountdownConfig>) {
    super(element, config, {
      defaultConfig,
      instanceKey: "ODS_CountdownTag",
    });
    this.units = new Map();
    this.init();
  }

  private getLabels(): Required<CountdownLabels> {
    return {
      ...defaultLabels,
      ...(this.config.labels ?? {}),
      days: normalizeDayLabels(this.config.labels?.days, defaultDayLabels),
    };
  }

  protected collectElements(): void {
    this.units.clear();

    const visibleUnits = getVisibleUnits(this.config.units);

    Array.from(
      this.element.querySelectorAll<HTMLElement>(SELECTOR_TAG_UNIT),
    ).forEach((unitElement) => {
      const unit = unitElement.getAttribute(
        ATTR_COUNTDOWN_UNIT,
      ) as CountdownUnit | null;

      if (!unit || !visibleUnits.includes(unit)) return;

      this.units.set(unit, {
        element: unitElement,
        unit,
        valueElement: unitElement.querySelector(SELECTOR_TAG_VALUE),
      });
    });
  }

  protected render(): void {
    const visibleUnits = getVisibleUnits(this.config.units);
    const labels = this.getLabels();
    const state = calculateState(this.config.targetDate, visibleUnits);

    visibleUnits.forEach((unit) => {
      const currentUnit = this.units.get(unit);

      if (!currentUnit) {
        return;
      }

      const value = state[unit];

      if (currentUnit.valueElement) {
        currentUnit.valueElement.textContent = formatValue(value);
      }

      const labelElement =
        currentUnit.element.querySelector(SELECTOR_TAG_LABEL);
      if (labelElement) {
        labelElement.textContent = getUnitLabel(unit, labels);
      }
    });

    if (this.liveRegion) {
      this.liveRegion.textContent = visibleUnits
        .map((unit) => `${state[unit]} ${getUnitLabel(unit, labels)}`)
        .join(", ");
    }
  }

  protected destroyElements(): void {}

  static getInstance(el: HTMLElement): CountdownTagStatic | null {
    return el && (el as any).ODS_CountdownTag
      ? (el as any).ODS_CountdownTag
      : null;
  }
}
